import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import {
  customOption,
  customSingleValue,
  LoadingSpinner,
} from "../../../constants/const";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";

const CustomSelect = ({
  label,
  options,
  value,
  onChange,
  loading,
  error,
  placeholder,
  disabled = false,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options}
      value={value}
      className="basic-select no-drag"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={disabled || options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      {...(error && { invalid: true })}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input type="text" value={value} onChange={onChange} className="no-drag" />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const getCleanNumber = (value) => String(value || "").replace(/[^0-9.]/g, "");

const getReceiptNumber = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const str = String(value).trim();
    if (str) {
      const parts = str.split(" ");
      return parts.length > 1 ? parts.slice(1).join(" ") : str;
    }
  }
  return "";
};

const getAmount = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;

    const cleanValue = String(value).replace(/,/g, "").trim();
    const numberValue = Number(cleanValue);

    if (!Number.isNaN(numberValue)) {
      return String(Math.abs(numberValue));
    }

    return cleanValue;
  }
  return "";
};

const getDateValue = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const parts = raw.split(/[-/]/).map(Number);

  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    if (String(parts[0]).length === 4) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  const parsedDate = new Date(raw);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const getMemoParts = (memo) => {
  const value = String(memo || "").trim();
  console.log("Parsing memo:", memo);
  // 1. extract quantity + price
  const match = value.match(/^[-]?(\d+)\s*x\s*(\d+)/i);

  if (!match) {
    return { memo: value, quantity: "", price: "" };
  }

  let quantity = String(parseInt(match[1], 10));
  let price = String(parseInt(match[2], 10));

  console.log(quantity, price, value);
  // 2. extract memo manually (everything after price)
  let afterPrice = value.split(/x\s*\d+/i)[1] || "";

  // 3. cleanup memo
  let memoPart = afterPrice
    .replace(/[()]/g, "")   // remove parentheses
    .trim();

  return {
    quantity,
    price,
    memo: memoPart || "",
  };
};

const DEFAULT_FORM = {
  selectedFinanceCategory: null,
  selectedAccount: null,
  selectedDimension: null,
  date: new Date(),
  amount: "",
  memo: "",
  quantity: "",
  price: "",
  serialNumber: "",
  receiptNumber: "",
};

const matchesOption = (option, values) => {
  const optionValues = [
    option?.value,
    option?.id,
    option?.label,
    option?.label1,
    option?.name,
    option?.title,
  ]
    .map((item) =>
      String(item || "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);

  return values.some((value) =>
    optionValues.includes(
      String(value || "")
        .trim()
        .toLowerCase(),
    ),
  );
};

const findOption = (options, values) =>
  options.find((option) => matchesOption(option, values)) || null;

const EditOtherIncome = ({
  setModal,
  transactionNo,
  transaction,
  unitId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toggle = () => setModal(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [financeDimensions, setFinanceDimensions] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    details: false,
    accounts: false,
    dimensions: false,
    submit: false,
  });

  const showQuantityPrice =
    formData.selectedFinanceCategory?.label1 === "asset" ||
    formData.selectedFinanceCategory?.label1 === "maktaba" ||
    formData.selectedFinanceCategory?.label1 === "misc";

  const isLoanIndividual =
    formData.selectedFinanceCategory?.label1 === "loan_individual";

  const isInfaqRafeeqat =
    formData.selectedFinanceCategory?.label1 === "infaq_rafeeqat";

  const isSingleEntry = isLoanIndividual || isInfaqRafeeqat;

  const isTarbiatiCourse =
    formData.selectedFinanceCategory?.label1 === "tarbiati_course";

  const isBusy = Object.values(loading).some(Boolean);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleReceiptNumber = (e) => {
    handleChange("receiptNumber", getCleanNumber(e.target.value));
  };

  const handleAmountChange = (e) => {
    handleChange("amount", getCleanNumber(e.target.value));
  };

  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setLoading((prev) => ({ ...prev, accounts: true, dimensions: true }));
    setFinanceAccounts([]);
    setFinanceDimensions([]);

    ApiFinance()
      .GetFinanceCategoryAccountsById(categoryId)
      .then((res) => {
        setFinanceAccounts(res.data || []);
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load income heads.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, accounts: false }));
      });

    ApiFinance()
      .GetFinanceCategoryDimensionsById(categoryId)
      .then((res) => {
        setFinanceDimensions(res.data || []);
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load income sub-heads.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, dimensions: false }));
      });
  }, [formData.selectedFinanceCategory]);

  useEffect(() => {
    if (financeAccounts.length === 0) return;

    const selectedAccount =
      findOption(financeAccounts, [
        transaction?.codeId,
        transaction?.accountName,
      ]) ||
      financeAccounts[0] ||
      null;

    setFormData((prev) => ({
      ...prev,
      selectedAccount,
    }));
  }, [financeAccounts, transaction]);

  useEffect(() => {
    if (isSingleEntry || financeDimensions.length === 0) {
      setFormData((prev) => ({
        ...prev,
        selectedDimension: null,
      }));
      return;
    }

    const selectedDimension =
      findOption(financeDimensions, [
        transaction?.dimension2Id,
        transaction?.subHeadName,
      ]) ||
      financeDimensions[0] ||
      null;

    setFormData((prev) => ({
      ...prev,
      selectedDimension,
    }));
  }, [financeDimensions, isLoanIndividual, transaction, isSingleEntry]);

  useEffect(() => {
    if (!transaction) return;

    const memoParts = getMemoParts(transaction.memo);

    setFormData((prev) => ({
      ...prev,
      date: getDateValue(transaction.date),
      amount: getAmount(transaction.amount),
      memo: memoParts.memo,
      quantity: memoParts.quantity,
      price: memoParts.price,
      serialNumber: transaction.serialNumber || "",
      receiptNumber: getReceiptNumber(transaction.receiptNumber, transaction.reference),
      selectedFinanceCategory: transaction.category || null,
    }));
  }, [transaction]);

  const validate = () => {
    const nextErrors = {
      amount: formData.amount ? (
        ""
      ) : (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      ),
      receiptNumber: formData.receiptNumber ? (
        ""
      ) : (
        <GetLanguageString props="common_field_required_error" />
      ),
    };

    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => value === "");
  };

  const getFormattedMemo = () => {
    if (!showQuantityPrice) return formData.memo;

    const quantity = formData.quantity || "0000";
    const price = formData.price || "0000";
    return `-${String(quantity).padStart(4, "0")} x ${String(price).padStart(4, "0")} (${formData.memo})`;
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const payload = {
      transactionNo,
      financeAccount: formData.selectedAccount ?? null,
      dimension2: isSingleEntry
        ? null
        : (formData.selectedDimension ?? null),
      category: formData.selectedFinanceCategory ?? null,
      date: formData.date,
      amount: formData.amount,
      serialNumber: formData.serialNumber,
      memo: getFormattedMemo(),
      unitId: unitId || transaction?.unitId,
      receiptNumber: formData.receiptNumber,
    };

    setLoading((prev) => ({ ...prev, submit: true }));

    ApiFinanceLocal()
      .EditOtherIncome(payload)
      .then((res) => {
        const data = res.data || {};

        if (Number(data.statusCode) !== 200) {
          swal({
            title: "Error",
            text: data.message || "Failed to update Other Income",
            icon: "error",
            buttons: "OK",
          });
          return;
        }

        swal({
          title: "Success",
          text: t(data.message) || t("member_member_detail_fund_edited"),
          icon: "success",
          buttons: "OK",
        }).then(() => {
          setModal(false);
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to update Other Income.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  return (
    <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
      <Modal isOpen className="inmodal" size="xl" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="edit_infaq" />
        </ModalHeader>

        {isBusy ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              <Row>
                {isSingleEntry ? (
                  isLoanIndividual && (
                    <Col md="3">
                      <CustomInput
                        name="serialNumber"
                        label="notifications_rafiq_serial_number"
                        value={formData.serialNumber}
                        onChange={(e) =>
                          handleChange("serialNumber", e.target.value)
                        }
                        error={errors.serialNumber}
                      />
                    </Col>
                  )
                ) : isTarbiatiCourse ? (
                  <Col md="3">
                    <CustomSelect
                      label="funds_income_head"
                      options={financeAccounts}
                      value={formData.selectedAccount}
                      onChange={(value) =>
                        handleChange("selectedAccount", value)
                      }
                      error={errors.selectedAccount}
                      loading={loading.accounts}
                      placeholder={
                        loading.accounts
                          ? t("finance_other_income_income_heads_loading")
                          : financeAccounts.length > 0
                            ? t("finance_other_income_income_heads_select")
                            : t("finance_other_income_income_heads_empty")
                      }
                    />
                  </Col>
                ) : (
                  <>
                    <Col md="3">
                      <CustomSelect
                        label="funds_income_head"
                        options={financeAccounts}
                        value={formData.selectedAccount}
                        onChange={(value) =>
                          handleChange("selectedAccount", value)
                        }
                        error={errors.selectedAccount}
                        loading={loading.accounts}
                        placeholder={
                          loading.accounts
                            ? t("finance_other_income_income_heads_loading")
                            : financeAccounts.length > 0
                              ? t("finance_other_income_income_heads_select")
                              : t("finance_other_income_income_heads_empty")
                        }
                      />
                    </Col>
                    <Col md="3">
                      <CustomSelect
                        label="funds_income_sub_head"
                        options={financeDimensions}
                        value={formData.selectedDimension}
                        onChange={(value) =>
                          handleChange("selectedDimension", value)
                        }
                        error={errors.selectedDimension}
                        loading={loading.dimensions}
                        placeholder={
                          loading.dimensions
                            ? t("finance_other_income_income_sub_heads_loading")
                            : financeDimensions.length > 0
                              ? t(
                                "finance_other_income_income_sub_heads_select",
                              )
                              : t("finance_other_income_income_sub_heads_empty")
                        }
                      />
                    </Col>
                  </>
                )}

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      maxDate={new Date()}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_receipt_number" />
                    </Label>
                    <Input
                      autoFocus
                      type="text"
                      className="no-drag"
                      value={formData.receiptNumber}
                      onChange={handleReceiptNumber}
                    />
                    {errors.receiptNumber && (
                      <h4 className="text-error">{errors.receiptNumber}</h4>
                    )}
                  </FormGroup>
                </Col>

                <Col md="3">
                  <CustomInput
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>

                <Col md="3">
                  <CustomInput
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={handleAmountChange}
                    error={errors.amount}
                  />
                </Col>

                {showQuantityPrice && (
                  <Col md="3" className="d-flex">
                    <div style={{ flex: "0 0 50%", paddingRight: "8px" }}>
                      <CustomInput
                        label="finance_other_income_quantity"
                        value={formData.quantity}
                        onChange={(e) =>
                          handleChange("quantity", e.target.value)
                        }
                        error={errors.quantity}
                      />
                    </div>

                    <div style={{ flex: "0 0 50%", paddingLeft: "8px" }}>
                      <CustomInput
                        label="finance_other_income_price"
                        value={formData.price}
                        onChange={(e) => handleChange("price", e.target.value)}
                        error={errors.price}
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button
                color="white"
                className={GetCustomStyles().btn_style}
                onClick={toggle}
              >
                <GetLanguageString props="common_cancel" />
              </Button>
              <Button
                color="primary"
                className={GetCustomStyles().btn_style}
                disabled={isBusy}
                type="submit"
              >
                <GetLanguageString props="common_save_changes" />
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditOtherIncome;
